import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { useRateLimitCountdown } from '../../hooks/useRateLimitCountdown';
import { parseBlockedUntil } from '../../utils/rateLimit';
import { cn } from '../../lib/utils';

/**
 * @param {{
 *   retryAfterSeconds?: number | null,
 *   blockedUntil?: string | null,
 *   expiresAt?: Date | null,
 *   size?: 'default' | 'large' | 'compact',
 *   showRetryAt?: boolean,
 *   className?: string,
 * }} props
 */
export function RateLimitCountdown({
  retryAfterSeconds = null,
  blockedUntil = null,
  expiresAt = null,
  size = 'default',
  showRetryAt = true,
  className,
}) {
  const resolvedExpiresAt = expiresAt || parseBlockedUntil(blockedUntil);
  const { secondsRemaining, canRetry, countdownLabel, retryAtLabel } =
    useRateLimitCountdown({
      retryAfterSeconds,
      expiresAt: resolvedExpiresAt,
    });

  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const lastAnnounceAtRef = useRef(0);
  const initialSecondsRef = useRef(secondsRemaining);

  useEffect(() => {
    if (canRetry) {
      setLiveAnnouncement('You can try again now.');
      return;
    }

    const now = Date.now();
    const isInitial = secondsRemaining === initialSecondsRef.current;
    const shouldAnnounce =
      isInitial || now - lastAnnounceAtRef.current >= 10000 || secondsRemaining <= 10;

    if (shouldAnnounce) {
      lastAnnounceAtRef.current = now;
      setLiveAnnouncement(
        `Try again in ${countdownLabel}. You can try again at ${retryAtLabel}.`
      );
    }
  }, [secondsRemaining, canRetry, countdownLabel, retryAtLabel]);

  if (canRetry) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        <span className="sr-only" aria-live="polite">
          {liveAnnouncement}
        </span>
        <span>You can try again now.</span>
      </div>
    );
  }

  const isLarge = size === 'large';
  const isCompact = size === 'compact';

  return (
    <div
      className={cn(
        'rounded-lg border border-border/60 bg-muted/40',
        isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2',
        className
      )}
    >
      <span className="sr-only" aria-live="polite">
        {liveAnnouncement}
      </span>
      <div
        className={cn(
          'flex items-start gap-2 text-muted-foreground',
          isCompact ? 'text-xs' : 'text-sm'
        )}
      >
        <Clock
          className={cn('shrink-0', isLarge ? 'mt-1 h-5 w-5' : 'mt-0.5 h-4 w-4')}
          aria-hidden="true"
        />
        <div className="min-w-0 space-y-0.5">
          <p>
            Try again in{' '}
            <span
              className={cn(
                'font-semibold tabular-nums text-foreground',
                isLarge && 'text-lg'
              )}
              aria-hidden="true"
            >
              {countdownLabel}
            </span>
          </p>
          {showRetryAt && retryAtLabel ? (
            <p className={cn(isCompact && 'text-ds-caption')}>
              You can try again at{' '}
              <span className="font-medium text-foreground">{retryAtLabel}</span>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
